// Journey-Stage Adaptive Dashboards — shared stage vocabulary + spine specs.
//
// The backend classifies a portal user's lifecycle stage for ALL FIVE account
// types (see the C# twin `PhoenixCapital.Core.Common.PortalJourneyStages`) and
// returns it as one of these string codes on each Home payload. The FE never
// re-derives a stage — it narrows the server string with the `asXxxStage`
// guards below and maps it onto a happy-path spine (branch stages such as
// "delinquent" fold onto their base step so the tracker stays linear — the
// hero/next-action carry the branch nuance). Keep these codes byte-identical
// to the C# constants.
//
// Unknown-stage safety: every `xxxSpine()` accepts the raw server string and
// falls back to the FIRST step for an unrecognized code instead of throwing —
// a newer backend must never crash an older bundle's Home. Prefer the
// `asXxxStage` guard when branching on the stage so unknown codes take the
// caller's explicit fallback path.

import type { StepperStep } from "@/design-system/components/ui";

export type BuyerStage =
  | "application"
  | "boarding"
  | "active-servicing"
  | "delinquent"
  | "paid-off";
export type SellerStage = "pre-loan" | "note-serviced" | "paid-off";
export type BrokerStage = "onboarding" | "active" | "inactive";
export type VendorStage = "onboarding" | "portal-active" | "connected" | "servicing";
export type TpsStage =
  | "pre-certification"
  | "certified-idle"
  | "stripe-onboarded"
  | "servicing";

export interface SpinePosition {
  index: number;
  complete: boolean;
}

const FALLBACK_POSITION: SpinePosition = { index: 0, complete: false };

function narrow<T extends string>(all: readonly T[], stage: string | null | undefined): T | null {
  return all.includes(stage as T) ? (stage as T) : null;
}

// ── Buyer ────────────────────────────────────────────────────────────────────
const BUYER_STAGES = [
  "application",
  "boarding",
  "active-servicing",
  "delinquent",
  "paid-off",
] as const satisfies readonly BuyerStage[];

export function asBuyerStage(stage: string | null | undefined): BuyerStage | null {
  return narrow(BUYER_STAGES, stage);
}

export const BUYER_SPINE: StepperStep[] = [
  { key: "application", label: "Application", hint: "Your loan is being prepared and finalized." },
  { key: "boarding", label: "Boarding", hint: "We're setting up servicing on your new loan." },
  { key: "servicing", label: "Servicing", hint: "Your loan is active — make your monthly payments here." },
  { key: "paid-off", label: "Paid off", hint: "Your loan is fully paid. Congratulations!" },
];
export function buyerSpine(stage: string): SpinePosition {
  switch (asBuyerStage(stage)) {
    case "application":
      return { index: 0, complete: false };
    case "boarding":
      return { index: 1, complete: false };
    case "active-servicing":
    case "delinquent":
      return { index: 2, complete: false };
    case "paid-off":
      return { index: 3, complete: true };
    default:
      return FALLBACK_POSITION;
  }
}

// ── Seller ───────────────────────────────────────────────────────────────────
const SELLER_STAGES = ["pre-loan", "note-serviced", "paid-off"] as const satisfies readonly SellerStage[];

export function asSellerStage(stage: string | null | undefined): SellerStage | null {
  return narrow(SELLER_STAGES, stage);
}

export const SELLER_SPINE: StepperStep[] = [
  { key: "pre-loan", label: "Deal in progress", hint: "Your sale is being underwritten and closed." },
  { key: "note-serviced", label: "Receiving payments", hint: "Your note is being serviced — payments flow to you monthly." },
  { key: "paid-off", label: "Paid off", hint: "The note has been paid in full." },
];
export function sellerSpine(stage: string): SpinePosition {
  switch (asSellerStage(stage)) {
    case "pre-loan":
      return { index: 0, complete: false };
    case "note-serviced":
      return { index: 1, complete: false };
    case "paid-off":
      return { index: 2, complete: true };
    default:
      return FALLBACK_POSITION;
  }
}

// ── Broker ───────────────────────────────────────────────────────────────────
// NOTE: the earlier "dormant" stage was removed end-to-end — no dormancy
// signal exists anywhere in the data (audit F-24), so neither twin emits it.
const BROKER_STAGES = ["onboarding", "active", "inactive"] as const satisfies readonly BrokerStage[];

export function asBrokerStage(stage: string | null | undefined): BrokerStage | null {
  return narrow(BROKER_STAGES, stage);
}

export const BROKER_SPINE: StepperStep[] = [
  { key: "onboarding", label: "Getting certified", hint: "Finish onboarding to unlock deal submission." },
  { key: "active", label: "Certified & active", hint: "You're certified — submit deals and earn commissions." },
];
export function brokerSpine(stage: string): SpinePosition {
  switch (asBrokerStage(stage)) {
    case "onboarding":
      return { index: 0, complete: false };
    // "inactive" (Suspended/Declined/…) deliberately does NOT render as a
    // completed journey — the account being shut off is not an achievement
    // (audit F-26). Tabs should suppress the spine entirely for inactive.
    case "active":
    case "inactive":
      return { index: 1, complete: false };
    default:
      return FALLBACK_POSITION;
  }
}

// ── Vendor ───────────────────────────────────────────────────────────────────
const VENDOR_STAGES = [
  "onboarding",
  "portal-active",
  "connected",
  "servicing",
] as const satisfies readonly VendorStage[];

export function asVendorStage(stage: string | null | undefined): VendorStage | null {
  return narrow(VENDOR_STAGES, stage);
}

export const VENDOR_SPINE: StepperStep[] = [
  // Qualification stages are ADVISORY — Phoenix records them and no gate
  // hangs off them, so the hint must not promise that completing them
  // unlocks anything (audit F-36).
  { key: "onboarding", label: "Qualifying", hint: "Phoenix is recording your qualification steps." },
  { key: "portal-active", label: "Ready", hint: "You're set up — Phoenix connects you to customers." },
  { key: "connected", label: "Connected", hint: "You're connected to customers and building the relationship." },
  { key: "servicing", label: "Servicing", hint: "You're actively reporting and getting paid." },
];
export function vendorSpine(stage: string): SpinePosition {
  switch (asVendorStage(stage)) {
    case "onboarding":
      return { index: 0, complete: false };
    case "portal-active":
      return { index: 1, complete: false };
    case "connected":
      return { index: 2, complete: false };
    case "servicing":
      return { index: 3, complete: false };
    default:
      return FALLBACK_POSITION;
  }
}

// ── TPS ──────────────────────────────────────────────────────────────────────
const TPS_STAGES = [
  "pre-certification",
  "certified-idle",
  "stripe-onboarded",
  "servicing",
] as const satisfies readonly TpsStage[];

export function asTpsStage(stage: string | null | undefined): TpsStage | null {
  return narrow(TPS_STAGES, stage);
}

export const TPS_SPINE: StepperStep[] = [
  { key: "pre-certification", label: "Certification", hint: "Get certified to service loans on the platform." },
  { key: "certified-idle", label: "Payout setup", hint: "Finish Stripe onboarding so funds can flow." },
  { key: "stripe-onboarded", label: "Ready", hint: "You're onboarded — accept assigned loans to start servicing." },
  { key: "servicing", label: "Servicing", hint: "You're actively servicing, billing, and collecting." },
];
export function tpsSpine(stage: string): SpinePosition {
  switch (asTpsStage(stage)) {
    case "pre-certification":
      return { index: 0, complete: false };
    case "certified-idle":
      return { index: 1, complete: false };
    case "stripe-onboarded":
      return { index: 2, complete: false };
    case "servicing":
      return { index: 3, complete: false };
    default:
      return FALLBACK_POSITION;
  }
}
